import { Component } from '@theme/component';
import { dashModelCodes, debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const RECENT_SEARCHES_KEY = 'paulstyle:recent-searches';
const GENDER_KEY = 'paulstyle:search-gender';
const MAX_RECENT_SEARCHES = 5;

/** Words the query parser understands, without diacritics. */
const GENDER_WORDS = {
  dama: 'Damă',
  dame: 'Damă',
  femei: 'Damă',
  femeie: 'Damă',
  barbati: 'Bărbați',
  barbat: 'Bărbați',
  barbatesti: 'Bărbați',
};
const TYPE_WORDS = {
  mocasini: 'Mocasini',
  ghete: 'Ghete',
  botine: 'Botine',
  cizme: 'Cizme',
  adidasi: 'Adidași',
  balerini: 'Balerini',
  sandale: 'Sandale',
  saboti: 'Saboți',
  bocanci: 'Bocanci',
};
const SIZE_WORDS = ['marime', 'marimea', 'marimi', 'nr', 'nr.', 'numar', 'numarul', 'masura'];
const KNOWN_WORDS = new Set([
  ...Object.keys(GENDER_WORDS),
  ...Object.keys(TYPE_WORDS),
  ...SIZE_WORDS,
  // Colours and everyday words, so they're never "corrected" into a similar model name (alba → Talba).
  ...'negru neagra negri negre alb alba albi albe maro maron bej visiniu visinie coniac rosu rosie rosii albastru albastra bleumarin gri verde roz galben galbena nude latte camel taupe kaki auriu aurii argintiu crem bordo multicolor'.split(' '),
  ...'pantofi sport eleganti elegante casual piele naturala lac velur intoarsa vara iarna toamna primavara blana imblaniti toc tocuri talpa inalte inalti joase comozi usori oferta oferte reducere reduceri ieftini noi nou noua cadou marimi tabel ghid livrare retur schimb comanda'.split(' '),
]);

/**
 * Lowercases and removes diacritics, so "Bărbați" and "barbati" compare equal.
 * @param {string} text
 */
function fold(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {number} The Levenshtein distance between a and b.
 */
function editDistance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i++) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
}

/**
 * @typedef {object} SearchChip
 * @property {string} label - What the shopper sees ("Mărimea 38").
 * @property {string} token - The word in the query the chip came from.
 * @property {'size' | 'gender' | 'type'} kind
 *
 * @typedef {object} SearchIntent
 * @property {string} query - The text sent to Shopify: the input without size words.
 * @property {string | null} size - A shoe size from the query, like "38".
 * @property {SearchChip[]} chips
 * @property {{ from: string, to: string } | null} correction - A misspelled model name that was fixed.
 */

/**
 * Reads what the shopper means from a query like "mocasini damă mărimea 38".
 * Sizes 34–47 become a size filter instead of search text, since every product has those
 * numbers in its variants. Misspelled model names ("luai") are swapped for the closest real one.
 * @param {string} raw - The input value.
 * @param {string[]} modelNames - Lowercase model names without diacritics.
 * @param {boolean} allowCorrection
 * @returns {SearchIntent}
 */
function parseSearchIntent(raw, modelNames, allowCorrection) {
  /** @type {SearchIntent} */
  const intent = { query: '', size: null, chips: [], correction: null };
  const words = [];

  for (const token of raw.trim().split(/\s+/)) {
    const folded = fold(token).replace(/[,.;]+$/, '');
    if (!folded) continue;

    const number = Number(folded);
    if (/^\d{2}$/.test(folded) && number >= 34 && number <= 47) {
      intent.size = folded;
      intent.chips.push({ label: `Mărimea ${folded}`, token, kind: 'size' });
      continue;
    }

    if (SIZE_WORDS.includes(folded)) continue;

    if (GENDER_WORDS[folded]) {
      intent.chips.push({ label: GENDER_WORDS[folded], token, kind: 'gender' });
    } else if (TYPE_WORDS[folded]) {
      intent.chips.push({ label: TYPE_WORDS[folded], token, kind: 'type' });
    } else if (allowCorrection && folded.length >= 4 && !/\d/.test(folded) && !KNOWN_WORDS.has(folded)) {
      if (!modelNames.includes(folded)) {
        const maxDistance = folded.length >= 6 ? 2 : 1;
        const match = modelNames.find(
          (name) => Math.abs(name.length - folded.length) <= maxDistance && editDistance(folded, name) <= maxDistance
        );
        if (match) {
          intent.correction = { from: token, to: match };
          words.push(match);
          continue;
        }
      }
    }

    words.push(token);
  }

  intent.query = dashModelCodes(words.join(' '));
  return intent;
}

/** @returns {string[]} */
function readRecentSearches() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} searches */
function writeRecentSearches(searches) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch {
    // Private mode or storage full: recent searches just won't be remembered.
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [intentBar] - Shows the filters read from the query and typo fixes.
 * @property {HTMLButtonElement} [viewAllButton] - The "see all results" button.
 * @property {HTMLFormElement} [form] - The search form.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {SearchIntent | null} */
  #intent = null;

  /** A query the shopper asked to search exactly as typed, without typo correction. */
  #exactQuery = '';

  /** @returns {string[]} Model names from the theme setting, lowercase and without diacritics. */
  get #modelNames() {
    return (this.dataset.modelNames || '')
      .split(',')
      .map((name) => fold(name.trim()))
      .filter((name) => name.length >= 3);
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, () => this.#refreshTouchScrolling(), { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.addEventListener('click', this.#handleShortcutClick, { signal });
    this.refs.predictiveSearchResults.addEventListener('touchmove', this.#dismissKeyboard, { signal, passive: true });
    this.refs.form?.addEventListener('submit', this.#handleSubmit, { signal });
    this.#afterRender();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const items = this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]');
    return /** @type {HTMLElement[]} */ (Array.from(items).filter((item) => item instanceof HTMLElement && !item.hidden));
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a, button')?.click();
        } else {
          event.preventDefault();
          this.#saveRecentSearch(this.refs.searchInput.value);
          window.location.href = this.#searchPageUrl();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    this.#runSearch();
  }, 200);

  /**
   * Searches for the input value: reads sizes, gender and type from it, fixes misspelled
   * model names, then fetches results for the rest.
   */
  #runSearch() {
    const value = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!value.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    const intent = parseSearchIntent(value, this.#modelNames, value !== this.#exactQuery);
    this.#intent = intent;
    this.#renderIntent();
    this.#updateViewAllButton();

    if (!intent.query) {
      // Only a size was typed ("42"): there's nothing to search for yet, the button opens all products in that size.
      this.#createAbortController();
      return;
    }

    this.#getSearchResults(intent.query);
  }

  /**
   * Shows the filters read from the query as removable chips, plus a note when a misspelled
   * model name was fixed.
   */
  #renderIntent() {
    const { intentBar } = this.refs;
    if (!intentBar) return;

    const intent = this.#intent;
    intentBar.replaceChildren();

    if (!intent || (intent.chips.length === 0 && !intent.correction)) {
      intentBar.hidden = true;
      return;
    }

    if (intent.correction) {
      const note = document.createElement('p');
      note.className = 'ps-intent__correction';
      const corrected = document.createElement('em');
      corrected.textContent = intent.correction.to.charAt(0).toUpperCase() + intent.correction.to.slice(1);
      const exact = document.createElement('button');
      exact.type = 'button';
      exact.className = 'ps-text-button';
      exact.dataset.exactQuery = '';
      exact.textContent = `Caută exact „${intent.correction.from}”`;
      note.append('Rezultate pentru ', corrected, '. ', exact);
      intentBar.append(note);
    }

    if (intent.chips.length > 0) {
      const row = document.createElement('div');
      row.className = 'ps-intent__chips';
      const label = document.createElement('span');
      label.className = 'ps-intent__label';
      label.textContent = 'Am filtrat după:';
      row.append(label);

      for (const chip of intent.chips) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ps-intent__chip ps-intent__chip--${chip.kind}`;
        button.dataset.removeToken = chip.token;
        button.setAttribute('aria-label', `Elimină filtrul ${chip.label}`);
        button.textContent = chip.label;
        row.append(button);
      }
      intentBar.append(row);
    }

    intentBar.hidden = false;
  }

  /**
   * Hides products that aren't in stock in the size from the query and marks the rest.
   */
  #applySizeFilter() {
    const size = this.#intent?.size;
    const results = this.refs.predictiveSearchResults;
    const rows = results.querySelectorAll('.ps-item--row[data-sizes]');
    if (!size || rows.length === 0) return;

    let visible = 0;
    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;
      const sizes = (row.dataset.sizes || '').split(',').map((value) => value.replace(/\D/g, ''));
      const inStock = sizes.includes(size);
      row.hidden = !inStock;
      const stock = row.querySelector('.ps-item__stock');
      if (stock instanceof HTMLElement) {
        stock.hidden = !inStock;
        stock.textContent = `Mărimea ${size} în stoc`;
      }
      if (inStock) visible++;
    }

    const title = results.querySelector('[data-ps-products-title]');
    if (title) title.textContent = `Produse în mărimea ${size}`;

    const empty = results.querySelector('[data-ps-size-empty]');
    if (empty instanceof HTMLElement) {
      empty.hidden = visible > 0;
      empty.textContent = `Primele rezultate nu sunt în stoc în ${size}. Apasă „Vezi toate” pentru toate modelele în mărimea ${size}.`;
    }
  }

  /** @returns {string} The search page URL for the current query, with the size as a filter. */
  #searchPageUrl() {
    const value = this.refs.searchInput.value.trim();
    // Parsed again here: the button can be pressed before the debounced search has run.
    const intent = parseSearchIntent(value, this.#modelNames, value !== this.#exactQuery);
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', intent.query || '*');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('type', 'product');
    if (intent.size) {
      url.searchParams.set('filter.v.option.size', intent.size);
      url.searchParams.set('filter.v.availability', '1');
    }
    return url.toString();
  }

  #updateViewAllButton() {
    const { viewAllButton } = this.refs;
    if (!viewAllButton) return;
    const size = this.#intent?.size;
    viewAllButton.textContent = size ? `Vezi toate în mărimea ${size}` : 'Vezi toate rezultatele';
  }

  /**
   * The form submits with the button at the bottom or the keyboard's search key.
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    event.preventDefault();
    const value = this.refs.searchInput.value.trim();
    if (!value) return;
    this.#saveRecentSearch(value);
    window.location.href = this.#searchPageUrl();
  };

  /**
   * Handles the shortcuts in the popup: popular and recent searches, suggestions, the
   * Damă / Bărbați switch, filter chips and typo fixes. Also remembers the query when a
   * result is opened.
   * @param {MouseEvent} event
   */
  #handleShortcutClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const queryButton = target.closest('[data-query]');
    if (queryButton instanceof HTMLElement && queryButton.dataset.query) {
      event.preventDefault();
      this.#searchFor(queryButton.dataset.query);
      return;
    }

    const genderButton = target.closest('[data-gender-option]');
    if (genderButton instanceof HTMLElement && genderButton.dataset.genderOption) {
      try {
        localStorage.setItem(GENDER_KEY, genderButton.dataset.genderOption);
      } catch {
        // Not remembered; the switch still works for this visit.
      }
      this.#applyGender();
      return;
    }

    if (target.closest('[data-ps-recent-clear]')) {
      writeRecentSearches([]);
      this.#renderRecentSearches();
      return;
    }

    const removeButton = target.closest('[data-remove-token]');
    if (removeButton instanceof HTMLElement && removeButton.dataset.removeToken !== undefined) {
      const token = removeButton.dataset.removeToken;
      const words = this.refs.searchInput.value.trim().split(/\s+/);
      const index = words.indexOf(token);
      if (index >= 0) words.splice(index, 1);
      // A size chip also drops a "mărimea" right before it.
      if (index > 0 && SIZE_WORDS.includes(fold(words[index - 1] || ''))) words.splice(index - 1, 1);
      this.#searchFor(words.join(' '));
      return;
    }

    if (target.closest('[data-exact-query]')) {
      this.#exactQuery = this.refs.searchInput.value.trim();
      this.#runSearch();
      return;
    }

    if (target.closest('.predictive-search-form__content a[href]')) {
      this.#saveRecentSearch(this.refs.searchInput.value);
    }
  };

  /**
   * Closes the phone keyboard when the shopper starts scrolling the results. With the keyboard
   * open, iOS pans the locked page instead of scrolling the list.
   */
  #dismissKeyboard = () => {
    if (document.activeElement === this.refs.searchInput) this.refs.searchInput.blur();
  };

  /** @param {string} query */
  #searchFor(query) {
    this.refs.searchInput.value = query;
    this.refs.searchInput.focus();
    this.#runSearch();
  }

  /** @param {string} query */
  #saveRecentSearch(query) {
    const value = query.trim();
    if (value.length < 2) return;
    const recent = readRecentSearches().filter((item) => fold(item) !== fold(value));
    writeRecentSearches([value, ...recent].slice(0, MAX_RECENT_SEARCHES));
  }

  #renderRecentSearches() {
    const results = this.refs.predictiveSearchResults;
    const section = results.querySelector('[data-ps-recent]');
    const list = results.querySelector('[data-ps-recent-list]');
    const template = results.querySelector('template[data-ps-recent-template]');
    if (!(section instanceof HTMLElement) || !list || !(template instanceof HTMLTemplateElement)) return;

    const recent = readRecentSearches();
    list.replaceChildren(
      ...recent.map((query) => {
        const item = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const button = item.querySelector('[data-query]');
        if (button instanceof HTMLElement) button.dataset.query = query;
        const text = item.querySelector('[data-ps-recent-text]');
        if (text) text.textContent = query;
        return item;
      })
    );
    section.hidden = recent.length === 0;
  }

  #applyGender() {
    const start = this.refs.predictiveSearchResults.querySelector('.ps-start');
    if (!(start instanceof HTMLElement)) return;

    let gender = 'dama';
    try {
      gender = localStorage.getItem(GENDER_KEY) === 'barbati' ? 'barbati' : 'dama';
    } catch {
      // Storage unavailable: default to Damă.
    }

    start.dataset.gender = gender;
    for (const option of start.querySelectorAll('[data-gender-option]')) {
      option.setAttribute('aria-pressed', String(option.getAttribute('data-gender-option') === gender));
    }
  }

  /** Runs after the popup content changes: fills in what the server can't render. */
  #afterRender() {
    this.#renderRecentSearches();
    this.#applyGender();
    this.#applySizeFilter();
    this.#refreshTouchScrolling();
  }

  /**
   * iOS Safari doesn't notice that the results became taller than the screen after content was
   * added, and ignores swipes until another scroll area is touched. Toggling overflow makes it
   * check again.
   */
  #refreshTouchScrolling() {
    const scroller = this.refs.predictiveSearchResults;
    requestAnimationFrame(() => {
      scroller.style.overflowY = 'hidden';
      void scroller.offsetHeight;
      scroller.style.overflowY = '';
    });
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '10');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#afterRender();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts().slice(0, 4);
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#intent = null;
    this.#exactQuery = '';
    this.#renderIntent();
    this.#updateViewAllButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#afterRender();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
